import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { SnackbarKey } from "notistack";
//#endregion
//#region components
import { useFloatingMessages } from "components/common";
//#endregion
import { CSSHelper, InvoiceWarningHelper, LocalizationHelperInstance } from "utils/helpers";

function useInvoiceAlerts() {
  const { enqueueMessage, closeMessage } = useFloatingMessages();
  const [alertKeys, setAlertKeys] = React.useState<SnackbarKey[]>();

  React.useEffect(() => {
    return () => {
      closeMessage();
    };
  }, []);

  const showWarnings = (
    status: WintSuperkollAPIModel.InvoiceStatus,
    sendMethod: WintSuperkollAPIModel.InvoiceSendMethod,
    warnings?: WintSuperkollAPIModel.Warning[]
  ) => {
    if (alertKeys?.length) {
      closeMessage();
    }

    setTimeout(() => {
      const currentWarnings = [];
      const shownCategories: number[] = InvoiceWarningHelper.getWarningCategoriesShownFor(sendMethod);

      switch (sendMethod) {
        case WintSuperkollAPIModel.InvoiceSendMethod.Email:
          warnings?.forEach((warning) => {
            if (shownCategories.includes(warning.category)) {
              currentWarnings.push(
                enqueueMessage(warning.message, CSSHelper.convertWarningLevel(warning.level), false)
              );
            }
          });

          if (status === WintSuperkollAPIModel.InvoiceStatus.NotSent) {
            currentWarnings.push(
              enqueueMessage(
                LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendInfoMessage),
                StyleEnums.DisplayType.Info
              )
            );
          } else if (status === WintSuperkollAPIModel.InvoiceStatus.Paid) {
            currentWarnings.push(
              enqueueMessage(
                LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendInfoMessageAlreadyPaid),
                StyleEnums.DisplayType.Info
              )
            );
          }
          break;
        case WintSuperkollAPIModel.InvoiceSendMethod.Print:
          const oldInvoiceWarning = warnings?.find(
            (w) => w.category === WintSuperkollAPIModel.WarningCategory.OldInvoice
          );
          if (oldInvoiceWarning) {
            currentWarnings.push(
              enqueueMessage(oldInvoiceWarning.message, CSSHelper.convertWarningLevel(oldInvoiceWarning.level), false)
            );
          } else {
            warnings?.forEach((warning) => {
              // These three are rendered inline in the "Skicka som" box (see previewSendOptionSection.tsx)
              // instead of as floating snackbars.
              if (
                [
                  WintSuperkollAPIModel.WarningType.MissingCustomerReferenceCode,
                  WintSuperkollAPIModel.WarningType.MissingCustomerElectronicIdentifiers,
                  WintSuperkollAPIModel.WarningType.SendAsEInvoice,
                ].some((w) => w === warning.type)
              )
                return;

              if (shownCategories.includes(warning.category))
                currentWarnings.push(
                  enqueueMessage(
                    warning.message,
                    CSSHelper.convertWarningLevel(warning.level ?? WintSuperkollAPIModel.WarningLevel.Warning),
                    false
                  )
                );
            });
          }
          break;
        case WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent:
          warnings?.forEach((warning) => {
            if (shownCategories.includes(warning.category)) {
              currentWarnings.push(
                enqueueMessage(warning.message, CSSHelper.convertWarningLevel(warning.level)),
                false
              );
            }
          });
          break;
      }
      setAlertKeys(currentWarnings);
    });
  };

  return { showWarnings };
}

export default useInvoiceAlerts;
